import React, { useState, useEffect } from 'react';
import { Modal, Table, Tag, Empty, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { pw9221f1Api, ApprovalHistory } from '../../../services/pw9221f1Api';

interface ApprovalHistoryModalProps {
  visible: boolean;
  docNo: string;
  onClose: () => void;
}

const ACTION_MAP: Record<string, { label: string; color: string }> = {
  SUBMIT:  { label: '送審', color: 'processing' },
  APPROVE: { label: '核准', color: 'success' },
  REJECT:  { label: '退回', color: 'error' },
};

// F921_30 規格五值（批次 5 修正）：''=尚未處理、V=已初核、P=已審核未複核、Y=已核准、N=不准假
// S/R 為已廢自創碼，僅供舊歷史紀錄顯示容忍
const STATUS_MAP: Record<string, string> = {
  '': '尚未處理', V: '已初核', P: '已審核未複核', Y: '已核准', N: '不准假',
  S: '送審中(舊碼)', R: '已退回(舊碼)',
};

const columns: ColumnsType<ApprovalHistory> = [
  {
    title: '時間', dataIndex: 'created_at', key: 'created_at', width: 170,
    render: (val: string) => {
      if (!val) return '-';
      try {
        return new Date(val).toLocaleString('zh-TW', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit',
        });
      } catch { return val; }
    },
  },
  {
    title: '動作', dataIndex: 'action', key: 'action', width: 90,
    render: (val: string) => {
      const info = ACTION_MAP[val] || { label: val, color: 'default' };
      return <Tag color={info.color}>{info.label}</Tag>;
    },
  },
  {
    title: '執行人', dataIndex: 'actor_name', key: 'actor_name', width: 100,
    render: (val: string, row: ApprovalHistory) => val || row.actor_id || '-',
  },
  {
    title: '原狀態', dataIndex: 'old_status', key: 'old_status', width: 80,
    render: (val: string) => STATUS_MAP[val] || val || '-',
  },
  {
    title: '新狀態', dataIndex: 'new_status', key: 'new_status', width: 80,
    render: (val: string) => STATUS_MAP[val] || val || '-',
  },
  {
    title: '備註', dataIndex: 'comment', key: 'comment', ellipsis: true,
    render: (val: string) => val || '-',
  },
];

const ApprovalHistoryModal: React.FC<ApprovalHistoryModalProps> = ({ visible, docNo, onClose }) => {
  const [data, setData] = useState<ApprovalHistory[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible || !docNo) return;
    let cancelled = false;
    setLoading(true);
    pw9221f1Api.getApprovalHistory(docNo)
      .then((result) => { if (!cancelled) setData(result); })
      .catch(() => { if (!cancelled) setData([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [visible, docNo]);

  return (
    <Modal
      title={`審核歷史 — ${docNo}`}
      open={visible} onCancel={onClose}
      width={720} destroyOnClose footer={null}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin tip="載入中..." /></div>
      ) : data.length === 0 ? (
        <Empty description="尚無審核紀錄" />
      ) : (
        <Table columns={columns} dataSource={data} rowKey="id" size="small" pagination={false} scroll={{ y: 400 }} />
      )}
    </Modal>
  );
};

export default ApprovalHistoryModal;
